// src/pages/AdminNews.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif', body: '"Barlow", sans-serif' };
const EMPTY = { title: '', excerpt: '', content: '', image_url: '', category: 'Community', author: 'BGN Editorial Team', featured: false, published: true };
const CATS  = ['Community', 'Gaming', 'Development', 'Health', 'Events', 'Spotlight', 'News'];

const AdminNews = () => {
  const { authToken }  = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState('');

  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/news`, { headers })
      .then(r => r.json()).then(d => setArticles(d.articles || []))
      .catch(() => showToast('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit   = a => { setEditing(a); setForm({ title: a.title, excerpt: a.excerpt||'', content: a.content||'', image_url: a.image_url||'', category: a.category||'Community', author: a.author||'BGN Editorial Team', featured: !!a.featured, published: a.published !== false }); setModal(true); };

  const save = async () => {
    if (!form.title.trim()) { showToast('Title is required'); return; }
    setSaving(true);
    try {
      const url    = editing ? `${BACKEND_URL}/api/admin/news/${editing.id}` : `${BACKEND_URL}/api/admin/news`;
      const method = editing ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      showToast(editing ? '✓ Article updated' : '✓ Article published');
      setModal(false); load();
    } catch { showToast('Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this article?')) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/news/${id}`, { method: 'DELETE', headers });
    if (res.ok) { showToast('Deleted'); load(); }
  };

  const S = {
    page:     { padding: '2.5rem' },
    btn:      { background: '#fff', color: '#000', border: 'none', padding: '0.58rem 1.4rem', fontFamily: font.mono, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
    btnGhost: { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.75rem', fontFamily: font.mono, fontSize: '0.6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
    input:    { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
    label:    { fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 5, display: 'block', textTransform: 'uppercase' },
  };

  return (
    <div style={S.page}>
      <style>{`
        @keyframes fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
        .overlay{position:fixed;inset:0;background:rgba(0,0,0,0.9);z-index:200;display:flex;align-items:flex-start;justify-content:center;padding:2rem;overflow-y:auto;}
        select option{background:#060606;}
      `}</style>

      {toast && <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background:'#111', border:'1px solid #222', color:'#fff', fontFamily:font.mono, fontSize:'0.7rem', padding:'0.75rem 1.25rem', zIndex:999, animation:'fadein 0.2s ease' }}>{toast}</div>}

      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:'1.5rem' }}>
        <div>
          <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', letterSpacing:'0.2em', marginBottom:6 }}>// MANAGE</div>
          <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'2.5rem', letterSpacing:'-0.02em' }}>News</div>
        </div>
        <button style={S.btn} onClick={openCreate}><Plus size={13} /> NEW ARTICLE</button>
      </div>

      {loading ? <p style={{ fontFamily:font.mono, fontSize:'0.68rem', color:'#333' }}>LOADING...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
          {articles.length === 0 && <p style={{ fontFamily:font.mono, fontSize:'0.68rem', color:'#333', padding:'2rem 0' }}>No articles yet.</p>}
          {articles.map(a => (
            <div key={a.id} style={{ background:'#0a0a0a', border:'1px solid #111', padding:'0.9rem 1.1rem', display:'flex', alignItems:'center', gap:'1rem' }}
              onMouseEnter={e => e.currentTarget.style.background='#0d0d0d'}
              onMouseLeave={e => e.currentTarget.style.background='#0a0a0a'}
            >
              {a.image_url && safeUrl(a.image_url) && <img src={safeUrl(a.image_url)} alt="" style={{ width:56, height:38, objectFit:'cover', filter:'grayscale(80%)', flexShrink:0 }} />}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2 }}>
                  <span style={{ fontFamily:font.body, fontSize:'0.88rem', color:'#ddd', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.title}</span>
                  {a.featured && <span style={{ background:'#fff', color:'#000', fontFamily:font.mono, fontSize:'0.48rem', padding:'1px 5px', flexShrink:0 }}>FEATURED</span>}
                  {!a.published && <span style={{ background:'#1a1a1a', color:'#555', fontFamily:font.mono, fontSize:'0.48rem', padding:'1px 5px', flexShrink:0 }}>DRAFT</span>}
                </div>
                <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333' }}>{a.category} · {a.author} · {new Date(a.created_at).toLocaleDateString()}</div>
              </div>
              <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                <button style={S.btnGhost} onClick={() => openEdit(a)}><Pencil size={10}/> Edit</button>
                <button style={{ ...S.btnGhost, color:'#ff6b6b', borderColor:'#2a1010' }} onClick={() => del(a.id)}><Trash2 size={10}/> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div style={{ background:'#0a0a0a', border:'1px solid #1a1a1a', padding:'2rem', width:'100%', maxWidth:660 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
              <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'1.4rem' }}>{editing ? 'EDIT ARTICLE' : 'NEW ARTICLE'}</div>
              <button onClick={() => setModal(false)} style={{ background:'none', border:'none', color:'#555', cursor:'pointer' }}><X size={16}/></button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.9rem' }}>
              <div><label style={S.label}>Title *</label><input style={S.input} value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="Article title" /></div>
              <div><label style={S.label}>Excerpt</label><input style={S.input} value={form.excerpt} onChange={e=>setForm(p=>({...p,excerpt:e.target.value}))} placeholder="Short summary" /></div>
              <div><label style={S.label}>Content</label><textarea style={{ ...S.input, height:160, resize:'vertical' }} value={form.content} onChange={e=>setForm(p=>({...p,content:e.target.value}))} placeholder="Full article content..." /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div><label style={S.label}>Image URL</label><input style={S.input} value={form.image_url} onChange={e=>setForm(p=>({...p,image_url:e.target.value}))} placeholder="https://..." /></div>
                <div><label style={S.label}>Category</label><select style={S.input} value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c}>{c}</option>)}</select></div>
              </div>
              <div><label style={S.label}>Author</label><input style={S.input} value={form.author} onChange={e=>setForm(p=>({...p,author:e.target.value}))} /></div>
              <div style={{ display:'flex', gap:'1.5rem' }}>
                <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontFamily:font.mono, fontSize:'0.62rem', color:'#666' }}><input type="checkbox" checked={form.featured} onChange={e=>setForm(p=>({...p,featured:e.target.checked}))}/> Featured</label>
                <label style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontFamily:font.mono, fontSize:'0.62rem', color:'#666' }}><input type="checkbox" checked={form.published} onChange={e=>setForm(p=>({...p,published:e.target.checked}))}/> Published</label>
              </div>
            </div>
            <div style={{ display:'flex', gap:'0.75rem', marginTop:'1.5rem' }}>
              <button style={S.btn} onClick={save} disabled={saving}>{saving ? 'SAVING...' : editing ? 'UPDATE' : 'PUBLISH'}</button>
              <button style={{ ...S.btn, background:'#111', color:'#666', border:'1px solid #1a1a1a' }} onClick={() => setModal(false)}>CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;